import { Avatar, AvatarProps, AvatarSize } from '@televet/kibble-ui';
import React, { MouseEvent, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { RouteBasePaths } from 'routes';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import {
  ClinicEntityCreationSource,
  PetParentAvatarFragment,
  useGetClinicPetParentAvatarPhoneNumbersQuery,
} from 'shared/types/graphql';
import { formatPhoneNumber, selectPrimaryPhoneNumberBestGuess } from 'shared/utils';
import { useSidePanelSearch } from '../../SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';

export interface PetParentAvatarProps extends AvatarProps {
  clinicPetParent?: (PetParentAvatarFragment & { displayName?: string }) | null;
  size?: AvatarSize;
  isGrouped?: boolean;
  index?: number;
  onRemove?: (e: MouseEvent) => void;
  clearConversationIdOnView?: boolean;
}

const PetParentAvatar = ({
  clinicPetParent,
  size = 'md',
  isGrouped = false,
  onRemove,
  index,
  clearConversationIdOnView,
  ...rest
}: PetParentAvatarProps): JSX.Element => {
  const { viewClinicPetParent, setCurrentClinicPetId, currentClinicPetParentId } = useSidePanelSearch();
  const { isMobile } = useResolutionProvider();
  const history = useHistory();
  const { firstName, lastName, isTest, creationSource, id, displayName } = clinicPetParent || {};
  const fullName = [firstName, lastName].filter((n) => !!n).join(' ');
  const isTestClient = !!isTest;
  const isInboundSms = creationSource === ClinicEntityCreationSource.InboundSms && !(firstName || lastName);

  // TODO (future): Prevent duplicate requests for phone numbers because `displayName` hasn't had a chance
  // to return over the network yet. See the BoardCard component for an example.
  const { data: phoneNumbersData } = useGetClinicPetParentAvatarPhoneNumbersQuery({
    variables: { clinicPetParentId: id || '' },
    skip: !isInboundSms || !!displayName /* Pet parents with display name already have creation source accounted for */,
  });

  const phoneTooltipContent = useMemo(() => {
    if (!phoneNumbersData) return;

    const phoneNumber = selectPrimaryPhoneNumberBestGuess(
      phoneNumbersData?.findUniqueClinicPetParent?.phoneNumbers || [],
    );
    return `Text messages sent from ${phoneNumber ? formatPhoneNumber(phoneNumber) : 'unknown number'}`;
  }, [phoneNumbersData]);

  return (
    <Avatar
      size={size || 'lg'}
      name={displayName || fullName}
      tooltipPlacement="top"
      handleRemoveButtonClick={onRemove}
      data-testid="pet-parent-avatar"
      {...rest}
      handleClick={(e: MouseEvent): void => {
        // only clear pet id when changing pet parent
        if (currentClinicPetParentId !== id) {
          setCurrentClinicPetId('');
        }
        e.stopPropagation();
        viewClinicPetParent({ clinicPetParentId: id || '', clearConversationId: clearConversationIdOnView });
        if (isMobile) {
          history.push(RouteBasePaths.Conversations);
        }
      }}
      {...(isGrouped && { marginInlineStart: index === 0 ? 0 : '-2' })}
      {...(!clinicPetParent && {
        name: undefined,
        src: undefined,
        showTooltip: false,
      })}
      {...(isTestClient && {
        variant: 'outlineYellow',
        tooltipText: `${[firstName, lastName].filter((n) => !!n).join(' ')} (Test Client)`,
      })}
      {...(isInboundSms && {
        iconName: 'cellphone',
        tooltipText: phoneTooltipContent,
      })}
    />
  );
};

export default PetParentAvatar;
